import { useEffect, useRef } from 'react';
import { useSymbols } from '../market/useSymbols';
import { usePrefsValue } from '@adiox/ui';
import { useStreamStore } from '../stream/stream';
import { describeEvent, soundOf, describeTransition, type ConnectionView } from './describe';
import { notifications } from './notificationStore';

/**
 * Turns the live stream into notifications: every new engine event worth telling, and changes of the connection and the price
 * feed. It renders nothing. The user's "show toasts" setting is applied to the store here.
 */
export function NotificationFeed() {
  const store = useStreamStore();
  const symbols = useSymbols();
  const digits = useRef(new Map<string, number>());
  const toasts = usePrefsValue((s) => s.data.notifyToasts !== false);

  useEffect(() => {
    digits.current = new Map((symbols.data ?? []).map((s) => [s.name, s.digits]));
  }, [symbols.data]);

  useEffect(() => notifications.getState().setToastsEnabled(toasts), [toasts]);

  useEffect(() => {
    const initial = store.getState();
    let lastSeq = initial.recentEvents.at(-1)?.seq ?? 0;
    let previous: ConnectionView = { hubState: initial.hubState, feedUp: initial.feedUp };
    let wasLive = initial.hubState === 'connected';
    return store.subscribe((state) => {
      for (const logged of state.recentEvents) {
        if (logged.seq <= lastSeq) continue;
        lastSeq = logged.seq;
        const draft = describeEvent(logged.event, (symbol) => digits.current.get(symbol) ?? 5);
        const sound = soundOf(logged.event);
        if (draft) notifications.getState().add(sound ? { ...draft, sound } : draft);
      }
      const next: ConnectionView = { hubState: state.hubState, feedUp: state.feedUp };
      if (next.hubState !== previous.hubState || next.feedUp !== previous.feedUp) {
        const draft = describeTransition(previous, next, wasLive);
        if (draft) notifications.getState().add(draft);
        if (next.hubState === 'connected') wasLive = true;
        previous = next;
      }
    });
  }, [store]);

  return null;
}
